// src/services/liff.js
import { LIFF_CONFIG } from '../config';
import liff from '@line/liff';

class LiffService {
  constructor() {
    this.isInitialized = false;
    this.initializationPromise = null;
    this.liff = liff;
  }

  async initialize() {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._doInitialize();
    return this.initializationPromise;
  }

  async _doInitialize() {
    try {

      if (!LIFF_CONFIG.LIFF_ID) {
        throw new Error(`LIFF IDが設定されていません。環境変数REACT_APP_LIFF_IDを確認してください。実際の値: ${LIFF_CONFIG.LIFF_ID}`);
      }

      console.log('LIFF初期化開始:', LIFF_CONFIG.LIFF_ID);
      await this.liff.init({ liffId: LIFF_CONFIG.LIFF_ID });
      console.log('LIFF初期化完了');

      if (!this.liff.isLoggedIn()) {
        console.log('ログインが必要です');
        this.initializationPromise = null;
        this.liff.login();
        return;
      }

      console.log('ログイン済みです');
      this.isInitialized = true;

      // LINE側の仕様によりログイン後にgetProfile()の呼び出しが必要
      try {
        const profile = await this.liff.getProfile();
        console.log('プロフィール取得完了:', profile.userId);
      } catch (profileError) {
        console.warn('プロフィール取得に失敗しました:', profileError);
      }
    } catch (error) {
      console.error('LIFF initialization failed:', error);

      // 詳細なエラー情報を提供
      if (error.code) {
        switch (error.code) {
          case 'INIT_FAILED':
            throw new Error('LIFF IDが無効です。正しいLIFF IDを設定してください。');
          case 'INVALID_ARGUMENT':
            throw new Error('無効な引数が指定されました。LIFF IDを確認してください。');
          default:
            throw new Error(`LIFF初期化エラー (${error.code}): ${error.message}`);
        }
      }

      throw new Error('LINE内でアプリを起動しているか、LIFF IDが正しく設定されているか確認してください。');
    }
  }

  getIDToken() {
    if (!this.isInitialized || !this.liff) {
      throw new Error('LIFFが初期化されていません');
    }
    return this.liff.getIDToken();
  }

  async getProfile() {
    if (!this.isInitialized || !this.liff) {
      throw new Error('LIFFが初期化されていません');
    }
    return await this.liff.getProfile();
  }

  logout() {
    if (this.liff) {
      this.liff.logout();
    }
  }

  login() {
    if (this.liff) {
      console.log('強制再ログインを実行します');
      this.isInitialized = false;
      this.initializationPromise = null;
      // 現在のページをリダイレクト先として指定し、ログイン後に戻る
      this.liff.login({ redirectUri: window.location.href });
    }
  }

  isInClient() {
    return this.liff ? this.liff.isInClient() : false;
  }

  openWindow(url, external = false) {
    if (this.liff) {
      this.liff.openWindow({ url, external });
    }
  }

  closeWindow() {
    if (this.liff) {
      this.liff.closeWindow();
    }
  }

  get ready() {
    return this.isInitialized;
  }

  get version() {
    return this.liff ? this.liff.getVersion() : null;
  }

  // デバッグ情報を取得
  getDebugInfo() {
    return {
      isInitialized: this.isInitialized,
      liffId: LIFF_CONFIG.LIFF_ID,
      hasLiffObject: !!this.liff,
      isLoggedIn: this.liff ? this.liff.isLoggedIn() : false,
      isInClient: this.liff ? this.liff.isInClient() : false,
      version: this.version
    };
  }
}

const liffService = new LiffService();
export default liffService;